import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

/**
 * Reusable password validation hook
 * @param root0
 * @param root0.translate
 * @param root0.language
 * @param root0.minLength
 * @returns {Object} Password validation rules and schema
 */
export function usePasswordValidation({ translate, language, minLength = 12 }) {

    // Rules list
    const rules = useMemo(() => [
        {
            key: 'size',
            label: translate('validator.size'),
            test: (v) => v.length >= minLength
        },
        {
            key: 'uppercase',
            label: translate('validator.uppercase'),
            test: (v) => /[A-Z]/.test(v)
        },
        {
            key: 'lowercase',
            label: translate('validator.lowercase'),
            test: (v) => /[a-z]/.test(v)
        },
        {
            key: 'figure',
            label: translate('validator.figure'),
            test: (v) => /\d/.test(v)
        },
        {
            key: 'special_character',
            label: translate('validator.special_character'),
            test: (v) => /[^\w\s]/.test(v)
        }
    ], [translate, minLength]);

    // Zod schema created from rules
    const passwordSchema = useMemo(() => {
        return z.string().superRefine((value, ctx) => {
            rules.forEach((rule) => {
                if (!rule.test(value)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: rule.label
                    });
                }
            });
        });
    }, [rules]);

    // UI validation state
    const [status, setStatus] = useState([]);

    const validate = (password) => {
        const result = rules.map((rule) => ({
            key: rule.key,
            label: rule.label,
            test: rule.test(password)
        }));
        setStatus(result);
        return result;
    };

    // Reset on language change
    useEffect(() => {
        setStatus((prev) =>
            prev.map((item) => {
                const rule = rules.find((r) => r.key === item.key);
                return {
                    ...item,
                    label: rule?.label ?? item.label
                };
            })
        );
    }, [language, rules]);

    return {
        passwordSchema,
        status,
        validate
    };
}